import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { createFileRoute } from '@tanstack/react-router'
import { IconChevronDownOutline18 } from 'nucleo-ui-outline-18'
import { useMemo, useState } from 'react'
import { PageHeader } from '~/components/ui/page-header'
import {
  FEATURE_CATEGORIES,
  FEATURE_STATUSES,
  type Feature,
  type FeatureCategory,
  type FeatureStatus,
  featureTotals,
} from '~/lib/admin-features'
import { titleFor } from '~/lib/route-title'
import { cn } from '~/lib/utils'

export const Route = createFileRoute('/admin/features')({
  component: AdminFeatures,
  head: () => ({ meta: [{ title: titleFor(msg`Features`) }] }),
})

const STATUS_ORDER: readonly FeatureStatus[] = ['shipped', 'in_progress', 'planned']

const StatusBadge = ({ status }: { status: FeatureStatus }) => {
  const { i18n } = useLingui()
  const meta = FEATURE_STATUSES[status]
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium text-[11px]"
      style={{ background: meta.bg, color: meta.fg }}
    >
      <span aria-hidden="true" className="size-1.5 rounded-full" style={{ background: meta.dot }} />
      {i18n._(meta.label)}
    </span>
  )
}

const SourceLabel = ({ feature }: { feature: Feature }) => {
  if (!feature.source) {
    return null
  }
  const { kind } = feature.source
  if (kind === 'website') {
    const { href } = feature.source
    return (
      <a
        className="text-muted-foreground text-xs underline-offset-2 hover:text-foreground hover:underline"
        href={href ?? '/'}
        rel="noreferrer"
        target="_blank"
      >
        <Trans>website</Trans> {href}
      </a>
    )
  }
  if (kind === 'code') {
    return (
      <span className="text-muted-foreground text-xs">
        <Trans>code</Trans> · {feature.source.path ?? ''}
      </span>
    )
  }
  return (
    <span className="text-muted-foreground text-xs">
      <Trans>notion</Trans> · {feature.source.title ?? 'roadmap'}
    </span>
  )
}

type StatusFilter = FeatureStatus | 'all'

const filterCategory = (category: FeatureCategory, filter: StatusFilter): FeatureCategory => {
  if (filter === 'all') {
    return category
  }
  return { ...category, features: category.features.filter((f) => f.status === filter) }
}

/** True when every feature in the category has shipped — its work is done. */
const isCategoryDelivered = (category: FeatureCategory): boolean =>
  category.features.length > 0 && category.features.every((f) => f.status === 'shipped')

function AdminFeatures() {
  const { t, i18n } = useLingui()
  const [filter, setFilter] = useState<StatusFilter>('all')

  const totals = useMemo(() => featureTotals(FEATURE_CATEGORIES), [])

  const filtered = useMemo(
    () =>
      FEATURE_CATEGORIES.map((category) => filterCategory(category, filter)).filter(
        (category) => category.features.length > 0
      ),
    [filter]
  )

  // Per-category collapse state. Initial expanded state is computed from the
  // unfiltered category (so a category that's fully delivered is collapsed by
  // default even when the filter would have made it look incomplete). Once the
  // user toggles a section, their choice sticks across filter changes.
  const [openByCategory, setOpenByCategory] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      FEATURE_CATEGORIES.map((category) => [category.id, !isCategoryDelivered(category)])
    )
  )
  const toggleCategory = (id: string) => {
    setOpenByCategory((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader meta={t`${totals.total} features tracked`} title={t`Features`}>
        <div className="flex flex-wrap items-center gap-2">
          <FilterPill
            active={filter === 'all'}
            label={`${t`All`} · ${totals.total}`}
            onClick={() => setFilter('all')}
            tone="#5e6678"
          />
          {STATUS_ORDER.map((status) => (
            <FilterPill
              active={filter === status}
              key={status}
              label={`${i18n._(FEATURE_STATUSES[status].label)} · ${totals[status]}`}
              onClick={() => setFilter(status)}
              tone={FEATURE_STATUSES[status].fg}
            />
          ))}
        </div>
      </PageHeader>

      {/* Progress bar */}
      <div className="px-5 pb-3">
        <div className="flex h-2 overflow-hidden rounded-full bg-muted">
          {STATUS_ORDER.map((status) => {
            const share = totals.total === 0 ? 0 : (totals[status] / totals.total) * 100
            return (
              <span
                key={status}
                style={{
                  background: FEATURE_STATUSES[status].dot,
                  width: `${share}%`,
                }}
              />
            )
          })}
        </div>
        <div className="mt-2 flex flex-wrap gap-4 text-muted-foreground text-xs">
          {STATUS_ORDER.map((status) => (
            <span className="inline-flex items-center gap-1.5" key={status}>
              <span
                className="size-2 rounded-full"
                style={{ background: FEATURE_STATUSES[status].dot }}
              />
              {i18n._(FEATURE_STATUSES[status].label)}: {totals[status]} (
              {totals.total === 0 ? 0 : Math.round((totals[status] / totals.total) * 100)}%)
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-6 px-5 pb-16">
        {filtered.map((category) => {
          const open = openByCategory[category.id] ?? true
          return (
            <section key={category.id}>
              <button
                aria-expanded={open}
                className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 pb-3 text-left transition-colors hover:text-foreground"
                onClick={() => toggleCategory(category.id)}
                type="button"
              >
                <IconChevronDownOutline18
                  aria-hidden="true"
                  className={cn(
                    'size-3.5 shrink-0 self-center text-muted-foreground transition-transform',
                    !open && '-rotate-90'
                  )}
                />
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: category.tone }}
                />
                <h2 className="font-semibold text-lg tracking-tight">{category.label}</h2>
                <span className="text-muted-foreground text-sm">{category.features.length}</span>
                {/* The blurb is the first thing to give way — on a phone the
                    category name and its count carry the row on their own. */}
                <p className="hidden text-muted-foreground text-sm sm:block">· {category.body}</p>
              </button>
              {open ? (
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                  {category.features.map((feature, i) => (
                    // Below `sm` the three columns stack: status chip, then the
                    // name/description block, then the source line.
                    <div
                      className={cn(
                        'grid grid-cols-1 items-start gap-2 px-4 py-3 sm:grid-cols-[120px_1fr_auto] sm:gap-4',
                        i > 0 && 'border-border border-t'
                      )}
                      key={feature.id}
                    >
                      {/* `sm:contents` dissolves the wrapper above `sm`, so the
                          badge is the grid item itself and keeps filling the
                          fixed status column exactly as before. Stacked, it
                          shrinks to its own width instead of spanning the row. */}
                      <div className="flex sm:contents">
                        <StatusBadge status={feature.status} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[14px] text-foreground">{feature.name}</p>
                        <p className="mt-1 text-[13px] text-muted-foreground leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                      <div className="min-w-0 break-words sm:shrink-0 sm:self-center sm:text-right">
                        <SourceLabel feature={feature} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          )
        })}
      </div>
    </div>
  )
}

const FilterPill = ({
  active,
  label,
  onClick,
  tone,
}: {
  active: boolean
  label: string
  onClick: () => void
  tone: string
}) => (
  <button
    className={cn(
      'rounded-md border px-3 py-1.5 font-medium text-xs transition',
      active
        ? 'border-foreground bg-foreground text-background'
        : 'border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground'
    )}
    onClick={onClick}
    style={active ? undefined : { color: tone }}
    type="button"
  >
    {label}
  </button>
)
